import { useContext, useCallback, useRef } from 'preact/hooks';
import { ChatContext } from '../hooks/context.js';
import { useChat } from '../hooks/useChat.js';
import { useSession } from '../hooks/useSession.js';
import {
  messages, isOpen, showTyping, showHistory, showReview,
  suggestions, toastMessage, setOpen,
} from '../store/chatStore.js';
import { MessageList } from '../components/MessageList.jsx';
import { TypingIndicator } from '../components/TypingIndicator.jsx';
import { QuickReplies } from '../components/QuickReplies.jsx';
import { Footer } from '../components/Footer.jsx';
import { ReviewPrompt } from '../components/ReviewPrompt.jsx';
import { HistoryPanel } from '../components/HistoryPanel.jsx';
import { ICONS } from '../utils/constants.js';
import { getContrastText } from '../services/colorEngine.js';

export function PanelLayout({ hostEl }) {
  const { cfg, bus, session } = useContext(ChatContext);
  const { send }              = useChat();
  const { startNewChat, loadSession } = useSession();
  const inputRef    = useRef(null);
  const colRef      = useRef(null);

  const scrollToBottom = useCallback(() => {
    const msgs = hostEl?.shadowRoot?.querySelector('.mc-gpanel-messages');
    if (msgs) msgs.scrollTop = msgs.scrollHeight;
  }, [hostEl]);

  const handleOpen = useCallback(() => {
    if (isOpen.value) return;
    setOpen(true);
    bus.emit('open', { sessionId: session.getOrCreate().sessionId });
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [bus, session]);

  const handleClose = useCallback(() => {
    if (!isOpen.value) return;
    showHistory.value = false;
    showReview.value  = false;
    setOpen(false);
    bus.emit('close', { sessionId: session.getOrCreate().sessionId });
  }, [bus, session]);

  const handleSend = useCallback((text) => {
    if (!isOpen.value) handleOpen();
    send(text);
  }, [send, handleOpen]);

  // Close button color derived from panel background
  const panelBg = cfg.floatpanelPanelBg || (cfg.theme === 'dark' ? '#0f0f1a' : '#ffffff');
  const contrastColor = getContrastText(panelBg);
  const closeBtnStyle = {
    color:      contrastColor,
    background: panelBg === '#ffffff' || panelBg === '#0f0f1a' ? 'rgba(128,128,128,0.15)' : `${panelBg}cc`,
    border:     `1px solid ${contrastColor === '#ffffff' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'}`,
  };

  const historyOpen = showHistory.value;

  return (
    <>
      {/* Full-viewport backdrop */}
      <div class="mc-gpanel-backdrop" aria-hidden="true" />

      {/* Top close button */}
      <button
        class="mc-gclose mc-btn-close"
        title="Close"
        aria-label="Close"
        style={closeBtnStyle}
        onClick={handleClose}
        dangerouslySetInnerHTML={{ __html: ICONS.close }}
      />

      {/* Centred column */}
      <div
        ref={colRef}
        class="mc-gcol"
        data-history-open={historyOpen ? '' : undefined}
      >
        {/* Messages (hidden when history open) */}
        {!historyOpen && (
          <MessageList messages={messages.value} isFloatpanel={true} />
        )}

        {/* History panel */}
        <HistoryPanel
          visible={historyOpen}
          isFloatpanel={true}
          onLoadSession={loadSession}
          onClose={() => { showHistory.value = false; }}
        />

        {/* Typing indicator */}
        <TypingIndicator visible={showTyping.value} isFloatpanel={true} />

        {/* Quick replies */}
        {suggestions.value.length > 0 && (
          <QuickReplies items={suggestions.value} onSend={handleSend} isFloatpanel={true} />
        )}

        {/* Review prompt */}
        <ReviewPrompt
          visible={showReview.value}
          isFloatpanel={true}
          onScrollBottom={scrollToBottom}
        />

        {/* Input bar */}
        <div class="mc-ginput-wrap" part="input-area">
          <div class="mc-ginput-row">
            {cfg.allowAttachments && (
              <button
                class="mc-attach-btn"
                title="Attach file"
                aria-label="Attach file"
                onClick={() => bus.emit('action', { action: 'attach', sessionId: session.getOrCreate().sessionId })}
                dangerouslySetInnerHTML={{ __html: ICONS.attach }}
              />
            )}
            <textarea
              ref={inputRef}
              class="mc-input"
              placeholder={cfg.floatpanelPlaceholder || cfg.placeholder}
              rows={1}
              aria-label="Type a message"
              maxLength={4000}
              onFocus={() => { if (!isOpen.value) handleOpen(); }}
              onInput={(e) => {
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, cfg.inputMaxRows * 22)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const val = e.target.value.trim();
                  if (val) {
                    e.target.value = '';
                    e.target.style.height = '';
                    handleSend(val);
                  }
                }
              }}
              disabled={historyOpen}
            />
            {/* Action icons — only shown when panel open */}
            <div class="mc-g-actions" aria-label="Chat actions">
              <button
                class="mc-g-icon-btn mc-btn-history"
                title="Chat history"
                aria-label="Chat history"
                onClick={() => { showHistory.value = !showHistory.value; }}
                dangerouslySetInnerHTML={{ __html: ICONS.history }}
              />
              <button
                class="mc-g-icon-btn mc-btn-newchat"
                title="New conversation"
                aria-label="New conversation"
                onClick={startNewChat}
                dangerouslySetInnerHTML={{ __html: ICONS.newchat }}
              />
            </div>
            <button
              class="mc-send-btn"
              aria-label="Send message"
              onClick={() => {
                const el = inputRef.current;
                const val = el?.value?.trim();
                if (val) { el.value = ''; el.style.height = ''; handleSend(val); }
              }}
              dangerouslySetInnerHTML={{ __html: ICONS.send }}
            />
          </div>
        </div>

        {cfg.showBranding && <Footer isFloatpanel={true} />}
      </div>

      {toastMessage.value && (
        <div class="mc-toast" style={{ position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          {toastMessage.value}
        </div>
      )}
    </>
  );
}
